import {
  Button,
  Checkbox,
  cn,
  ConfirmableDeleteButton,
  Icon,
  Input,
  Select,
  Text,
} from '@risksmart-app/atomic-ui';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import type { ConditionalLogicOption } from '../types';

export const ConditionalLogicRuleBuilder = ({
  fieldOptions,
  getValueOptions,
  currentEditingId,
  description,
  errorMessage,
}: {
  fieldOptions: ConditionalLogicOption[];
  getValueOptions?: (fieldValue: string) => ConditionalLogicOption[];
  currentEditingId?: string;
  description?: string;
  errorMessage?: string | null;
}) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'conditionalLogicRules',
  });

  const filteredFieldOptions = fieldOptions.filter(
    (opt) =>
      opt.value !== currentEditingId &&
      // Undefined → allow; only an explicit false rules a field out.
      opt.allowAsSource !== false
  );

  return (
    <div className={cn('flex flex-col gap-4 pt-4')}>
      <Text preset={'body-md'} className={cn('font-semibold m-0')}>
        {description ??
          'Show this section based on the output from another field'}
      </Text>
      {fields.map((rule, index) => {
        const selectedIfField = watch(`conditionalLogicRules.${index}.ifField`);
        const valueOptions = getValueOptions?.(selectedIfField) ?? [];
        const selectedFieldMeta = filteredFieldOptions.find(
          (opt) => opt.value === selectedIfField
        );
        const isFreeText = selectedFieldMeta?.freeText === true;

        const allSelectedIfFields = fields
          .map((_, i) => watch(`conditionalLogicRules.${i}.ifField`))
          .filter(Boolean);

        const availableFieldOptions = filteredFieldOptions.filter(
          (opt) =>
            opt.value === selectedIfField ||
            !allSelectedIfFields.includes(opt.value)
        );

        const ruleError = (
          errors.conditionalLogicRules as Record<string, unknown>[] | undefined
        )?.[index];

        return (
          <div
            key={rule.id}
            className={cn(
              'rounded-lg border p-4 flex flex-col gap-3 bg-neutral-minimal',
              ruleError ? 'border-destructive' : 'border-neutral-border'
            )}
          >
            <div className={cn('flex items-center justify-between')}>
              <Text preset={'body-md'} className={'font-semibold'}>
                {`Rule ${index + 1}`}
              </Text>
              <ConfirmableDeleteButton
                onConfirm={() => remove(index)}
                size={'icon'}
              />
            </div>
            <Controller
              control={control}
              name={`conditionalLogicRules.${index}.ifField`}
              render={({ field }) => (
                <div className={cn('flex items-center gap-3')}>
                  <Text
                    preset={'body-sm'}
                    className={'font-semibold shrink-0 w-[60px]'}
                  >
                    {'If'}
                  </Text>
                  <Select
                    items={[
                      {
                        label:
                          availableFieldOptions.length > 0
                            ? 'Pick a field'
                            : 'No valid fields to pick from',
                        value: null,
                      },
                      ...availableFieldOptions,
                    ]}
                    value={field.value || undefined}
                    onValueChange={(v) => {
                      field.onChange(v);
                      setValue(`conditionalLogicRules.${index}.values`, []);
                    }}
                    disabled={availableFieldOptions.length === 0}
                  />
                </div>
              )}
            />
            {selectedIfField && isFreeText ? (
              <Controller
                control={control}
                name={`conditionalLogicRules.${index}.values`}
                render={({ field: valuesField }) => (
                  <div className={cn('flex items-center gap-3')}>
                    <Text
                      preset={'body-sm'}
                      className={'font-semibold shrink-0 w-[60px]'}
                    >
                      {'Is equal to'}
                    </Text>
                    <Input
                      value={(valuesField.value ?? [])[0] ?? ''}
                      onChange={(e) =>
                        valuesField.onChange(
                          e.target.value === '' ? [] : [e.target.value]
                        )
                      }
                      placeholder={'Enter value'}
                    />
                  </div>
                )}
              />
            ) : null}
            {selectedIfField && !isFreeText && valueOptions.length > 0 ? (
              <Controller
                control={control}
                name={`conditionalLogicRules.${index}.values`}
                render={({ field: valuesField }) => {
                  const toggleValue = (val: string) => {
                    const current = valuesField.value ?? [];
                    const next = current.includes(val)
                      ? current.filter((v: string) => v !== val)
                      : [...current, val];
                    valuesField.onChange(next);
                  };

                  return (
                    <div className={cn('flex items-start gap-3')}>
                      <Text
                        preset={'body-sm'}
                        className={'font-semibold shrink-0 w-[60px] mt-4'}
                      >
                        {'Is equal to'}
                      </Text>
                      <div
                        className={cn(
                          'flex-1 rounded-md border border-neutral-border p-3 flex flex-col gap-2 bg-neutral'
                        )}
                      >
                        <Text preset={'body-md'}>
                          {'Select trigger inputs:'}
                        </Text>
                        {valueOptions.map((option) => (
                          <label
                            key={option.value}
                            className={cn(
                              'flex items-center gap-2 rounded py-1 pl-1.5 text-sm cursor-pointer'
                            )}
                          >
                            <Checkbox
                              size={'md'}
                              checked={(valuesField.value ?? []).includes(
                                option.value
                              )}
                              onCheckedChange={() => toggleValue(option.value)}
                            />
                            {option.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                }}
              />
            ) : null}
            {ruleError ? (
              <Text preset={'body-sm'} className={cn('text-destructive pt-4')}>
                {'This rule is invalid'}
              </Text>
            ) : null}
          </div>
        );
      })}
      <Button
        style={'dashed-fill'}
        radius={'xl'}
        className={'w-full'}
        onClick={() => append({ ifField: '', values: [] })}
      >
        <Icon name={'plus'} size={'sm'} />
        {'Add rule'}
      </Button>
      {errorMessage ? (
        <Text preset={'body-sm'} className={cn('text-destructive')}>
          {errorMessage}
        </Text>
      ) : null}
    </div>
  );
};
